import axios from 'axios';

// Get API URL from environment or use default
const getApiBaseUrl = () => {
    // Check if we have the Vite-defined API URL
    if (typeof __API_URL__ !== 'undefined') {
        return __API_URL__ + '/api';
    }
    
    // Fallback to environment variable or default
    return (import.meta.env.VITE_API_URL || 'http://localhost:3010') + '/api';
};

const instance = axios.create({
    baseURL: getApiBaseUrl(),
    headers: {
        'Content-Type': 'application/json'
    }
});

// Generate unique token key based on app name or environment variable
const getAuthTokenKey = () => {
    // Check for environment variable first
    if (import.meta.env.VITE_AUTH_TOKEN_KEY) {
        return import.meta.env.VITE_AUTH_TOKEN_KEY;
    }
    
    // Fallback to app-specific key
    const appName = import.meta.env.VITE_APP_NAME;
    return `${appName}_auth_token`;
};

// Surface plan-limit rejections (HTTP 402) app-wide. UpgradeModal listens for
// this event; callers still receive the rejected promise for inline handling.
const dispatchLimitEvent = (detail) => {
    window.dispatchEvent(new CustomEvent('fs:limit', { detail }));
};

// Response interceptor to handle authentication errors
instance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 402) {
            // Blob bodies (getBlob downloads) are parsed and dispatched there.
            if (!(error.response.data instanceof Blob)) dispatchLimitEvent(error.response.data);
            return Promise.reject(error);
        }
        // A 403 from the email test-send rail (address not on the server's
        // EMAIL_TEST_ALLOWLIST) is a domain rejection, not an auth failure —
        // logging the admin out for a mistyped test address would be brutal.
        if (error.response && error.response.status === 403
            && error.response.data && error.response.data.error === 'not_allowlisted') {
            return Promise.reject(error);
        }
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            // Token is invalid or expired
            console.log('Authentication failed, clearing token...');
            localStorage.removeItem(getAuthTokenKey());
            delete instance.defaults.headers.common['Authorization'];
            
            // Redirect to login if not already there
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Remove the need for initial '/' in endpoints
const formatEndpoint = endpoint => endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

const setAuthHeader = (token) => {
    if (token) {
        instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete instance.defaults.headers.common['Authorization'];
    }
};

const api = {
    setAuthHeader,
    
    get: async (endpoint, config = {}) => {
        try {
            const { data } = await instance.get(formatEndpoint(endpoint), config);
            return data;
        } catch (error) {
            console.error(`API Error: ${error.message}`);
            throw error;
        }
    },

    post: async (endpoint, body) => {
        try {
            const { data } = await instance.post(formatEndpoint(endpoint), body);
            return data;
        } catch (error) {
            console.error(`API Error: ${error.message}`);
            throw error;
        }
    },

    put: async (endpoint, body) => {
        try {
            const { data } = await instance.put(formatEndpoint(endpoint), body);
            return data;
        } catch (error) {
            console.error(`API Error: ${error.message}`);
            throw error;
        }
    },

    delete: async (endpoint) => {
        try {
            const { data } = await instance.delete(formatEndpoint(endpoint));
            return data;
        } catch (error) {
            console.error(`API Error: ${error.message}`);
            throw error;
        }
    },

    patch: async (endpoint, body) => {
        try {
            const { data } = await instance.patch(formatEndpoint(endpoint), body);
            return data;
        } catch (error) {
            console.error(`API Error: ${error.message}`);
            throw error;
        }
    },

    // Download a binary/text response — used for CSV export. Returns a Blob.
    // 402 responses are parsed from the blob and dispatched as fs:limit events
    // (the normal interceptor can't JSON-parse a blob error body).
    getBlob: async (endpoint, config = {}) => {
        try {
            const { data } = await instance.get(formatEndpoint(endpoint), { ...config, responseType: 'blob' });
            return data;
        } catch (error) {
            if (error.response && error.response.status === 402 && error.response.data instanceof Blob) {
                const text = await error.response.data.text();
                try { dispatchLimitEvent(JSON.parse(text)); } catch { dispatchLimitEvent({}); }
            }
            throw error;
        }
    },

    // Server-Sent Events for batched searches with progress
    getBatchedTraders: (params, onProgress, onComplete, onError) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
            const value = params[key];
            if (Array.isArray(value)) {
                value.forEach(item => searchParams.append(key, item));
            } else if (value !== undefined && value !== null) {
                searchParams.append(key, value);
            }
        });

        // Add auth token as query parameter since EventSource doesn't support headers
        const authToken = localStorage.getItem(getAuthTokenKey());
        if (authToken) {
            searchParams.append('token', authToken);
        }

        const url = `${getApiBaseUrl()}${formatEndpoint('traders/batched')}?${searchParams.toString()}`;
        
        const eventSource = new EventSource(url);
        
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('SSE received data:', data);
                
                switch (data.type) {
                    case 'progress':
                        console.log('SSE progress data:', data);
                        onProgress(data);
                        break;
                    case 'complete':
                        onComplete(data.data);
                        eventSource.close();
                        break;
                    case 'error':
                        // Plan-limit errors arrive in-band on SSE (headers already
                        // sent) — surface them the same way as HTTP 402s.
                        if (data.code === 'limit_exceeded') {
                            dispatchLimitEvent(data);
                        }
                        {
                            // code: e.g. 'place_too_wide' — an answer, not a fault.
                            const err = new Error(data.error);
                            err.code = data.code;
                            onError(err);
                        }
                        eventSource.close();
                        break;
                    default:
                        console.warn('Unknown SSE message type:', data.type);
                }
            } catch (error) {
                console.error('Error parsing SSE message:', error);
                onError(error);
                eventSource.close();
            }
        };
        
        eventSource.onerror = (error) => {
            console.error('SSE Error:', error);
            onError(error);
            eventSource.close();
        };
        
        return eventSource;
    }
};


export default api; 